import {useEffect, useRef, useState} from "react";
import './HotelNameDropdown.css';

const HotelIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1.66699 3.33301V16.6663" stroke="black" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M1.66699 6.66699H16.667C17.109 6.66699 17.5329 6.84259 17.8455 7.15515C18.1581 7.46771 18.3337 7.89163 18.3337 8.33366V16.667" stroke="black" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M1.66699 14.167H18.3337" stroke="black" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M5 6.66699V14.167" stroke="black" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const CloseIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 5L5 15" stroke="#02003C" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M5 5L15 15" stroke="#02003C" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

export default function SearchHotelName({placeholder, disabled = false, hotel, onChange}) {

    const [displayValue, setDisplayValue] = useState(hotel?.name || '')
    const [isFocused, setIsFocused] = useState(false)
    const [hotelNameError, setHotelNameError] = useState('')

    const inputRef = useRef(null);
    const labelText = placeholder || 'Nome do hotel (opcional)';
    const ariaLabel = labelText;

    const getHotelNameError = (value) => {
        const normalizedValue = (value || '').trim();

        if (!normalizedValue.length) {
            return '';
        }

        if (normalizedValue.length < 3) {
            return 'Digite ao menos 3 letras';
        }

        return '';
    }

    useEffect(() => {
        const value = hotel?.name || '';
        setDisplayValue(value)
        setHotelNameError(getHotelNameError(value))
    }, [hotel])

    useEffect(() => {
        if (disabled) {
            setIsFocused(false);
            setHotelNameError('');
        }
    }, [disabled])

    const handleChange = (e) => {
        const value = e.target.value;
        setDisplayValue(value);
        setHotelNameError(getHotelNameError(value));

        // Atualiza o estado com o nome do hotel em tempo real
        // O ID pode ser undefined pois o backend vai fazer a busca pelo nome
        onChange({ id: undefined, name: value });
    }

    const clearSelection = () => {
        setDisplayValue('');
        setHotelNameError('');
        onChange({ id: undefined, name: '' });

        if (inputRef.current) {
            inputRef.current.focus()
            setIsFocused(true)
        }
    }

    const handleBlur = () => {
        setIsFocused(false);
        setHotelNameError(getHotelNameError(displayValue));
    }

    const handleContainerClick = () => {
        if (disabled || !inputRef.current) return;
        inputRef.current.focus();
        setIsFocused(true);
    }

    const handleContainerKeyDown = (e) => {
        if (disabled) return;
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleContainerClick();
        }
    }

    const handleFocus = () => {
        setIsFocused(true);
    }

    const handleClearMouseDown = (e) => {
        e.preventDefault();
        clearSelection();
    }

    const hasValue = Boolean(displayValue && displayValue.trim().length);
    const hasError = Boolean(hotelNameError) && !disabled;

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    width: '100%',
                    minHeight: '62px',
                    padding: '16px',
                    border: '1px solid #D8D8D8',
                    borderRadius: '4px',
                    background: disabled ? '#f5f5f5' : 'transparent',
                    position: 'relative',
                    cursor: disabled ? 'not-allowed' : 'pointer'
                }}
                onClick={handleContainerClick}
                onKeyDown={handleContainerKeyDown}
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-disabled={disabled}
            >
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 2px', minWidth: '20px', minHeight: '20px' }}>
                    <HotelIcon />
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: hasValue ? '4px' : '0px', flex: 1, minWidth: 0 }}>
                    {hasValue && (
                        <label
                            style={{
                                color: '#4F4F4F',
                                letterSpacing: '-0.8px',
                                margin: 0,
                                lineHeight: '1',
                                fontSize: '12px',
                                fontFamily: 'Montserrat, sans-serif',
                                fontWeight: 400
                            }}
                        >
                            {labelText}
                        </label>
                    )}
                    <input
                        ref={inputRef}
                        className="form-control form-control-custom border-custom-none hotel-name-input"
                        value={displayValue}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        placeholder={!hasValue ? labelText : ""}
                        disabled={disabled}
                        id='hotelNameInput'
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                        aria-autocomplete="none"
                        aria-label={ariaLabel}
                        style={{
                            border: 'none',
                            boxShadow: 'none',
                            padding: 0,
                            height: !hasValue ? 'auto' : '20px',
                            fontSize: '14px',
                            fontWeight: !hasValue ? 400 : 600,
                            fontFamily: 'Montserrat, sans-serif',
                            color: '#4F4F4F',
                            background: 'transparent',
                            textOverflow: 'ellipsis'
                        }}
                    />
                </div>
                {displayValue && isFocused && !disabled && (
                    <button
                        type="button"
                        aria-label="Limpar"
                        onMouseDown={handleClearMouseDown}
                        className="btn btn-link p-0 m-0"
                        style={{ color: '#0B0D40', minWidth: '20px', position: 'absolute', right: 15, top: '50%', transform: 'translateY(-50%)' }}
                    >
                        <CloseIcon />
                    </button>
                )}
            </div>
            {hasError && (
                <div className="validation-error-message">{hotelNameError}</div>
            )}
        </div>
    )
}
